import React from "react";
//#region types
import { WintSuperkollAPIModel as ApiModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { OrganizationInfo } from "models/propTypes/userProfilePropTypes";
import { TranslationKeys } from "translations";
import { CustomerFormTypes } from "models/formTypes";
import { TableHandles, TableRowCommonKeys, TableRowProperties } from "models/propTypes/simpleTableProps";
import { CommonEnums, CurrencyEnums } from "models/enums";
import { Amount } from "models/utilTypes/currencyType";
import { CustomerListViewProps } from "models/propTypes/customerPropTypes";
//#endregion
//#region components
import { TableAsync, Tags, useFilterContext, useSearchContext } from "components/common";
import { useNavigate, useLocation } from "react-router-dom";
import { useRatingService, useCustomerService } from "serviceHooks";
//#endregion
import { CSSHelper, LocalizationHelperInstance, UrlHelper, UtilsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const CustomerList: React.ForwardRefRenderFunction<TableHandles, CustomerListViewProps> = (props, ref) => {
  const customerService = useCustomerService();
  const ratingService = useRatingService();
  const location = useLocation();
  const navigate = useNavigate();
  const { filter } = useFilterContext<CustomerFormTypes.Filter>();
  const searchContext = useSearchContext();

  const filterFunc = React.useCallback(
    (pagination: WintUtilsWebApi.Filter, key: number, skipUndefinedConversion?: boolean) => {
      const hasSearch = !!(searchContext?.searchText && searchContext.searchText.trim().length > 0);
      const composedFilter: CustomerFormTypes.Filter = {
        ...(filter ?? {}),
      } as CustomerFormTypes.Filter;
      if (hasSearch) {
        (composedFilter as any).customerName = searchContext!.searchText!;
      }
      return customerService.getCustomers(pagination, composedFilter, skipUndefinedConversion);
    },
    [filter, searchContext?.searchText]
  );

  const onRowClick = (row: ApiModel.Customer) => {
    navigate(UrlHelper.formatPath(`/Customer/${row.id}`), {
      state: { background: location, preserveSearch: true },
    });
  };

  const getRatingClass = (rating: number): string => {
    const ratingData = ratingService.getRatingInfo(CommonEnums.OrganizationTypes.Customer, rating);
    return CSSHelper.getTagClass(ratingData.displayType);
  };

  const tableProperties: Partial<Record<keyof TableRowCommonKeys | keyof ApiModel.Customer, TableRowProperties>> = {
    name: {
      description: TranslationKeys.Common.Name,
      helpText: TranslationKeys.Customer.HelpTextName,
      formatter: (r: ApiModel.Customer) => new OrganizationInfo(r.id, r.name ?? "", r.orgNumber),
      className: cssExports.iconProfileContainerFullHeight,
    },
    rating: {
      description: TranslationKeys.Common.Rating,
      helpText: TranslationKeys.Customer.HelpTextRating,
      formatter: (r: ApiModel.Customer) =>
        r.rating !== undefined ? <Tags tags={[`${r.rating}`]} className={getRatingClass(r.rating)}></Tags> : "",
      classification: "number",
    },
    numOfRecurringInvoices: {
      description: TranslationKeys.Common.RecurringInvoices,
      helpText: TranslationKeys.Customer.HelpTextRecurringInvoices,
      formatter: (r: ApiModel.Customer) => UtilsHelper.getCountText(r.numOfRecurringInvoices),
      alignment: "right",
      classification: "number",
    },
    numOfInvoices: {
      description: TranslationKeys.Common.NumOfInvoices,
      helpText: TranslationKeys.Customer.HelpTextSentInvoices,
      formatter: (r: ApiModel.Customer) => UtilsHelper.getCountText(r.numOfInvoices),
      alignment: "right",
      classification: "number",
    },
    purchasedAmount: {
      description: TranslationKeys.Common.Sale,
      helpText: TranslationKeys.Customer.HelpTextPurchasedAmount,
      formatter: (r: ApiModel.Customer) => new Amount(CurrencyEnums.CurrencyCode.SEK, r.purchasedAmount),
      alignment: "right",
    },
    unpaidAmount: {
      description: TranslationKeys.Common.Unpaid,
      helpText: TranslationKeys.Customer.HelpTextUnpaidAmount,
      formatter: (r: ApiModel.Customer) => new Amount(CurrencyEnums.CurrencyCode.SEK, r.unpaidAmount),
      className: cssExports.textBlack,
      alignment: "right",
    },
  };

  return (
    <TableAsync<ApiModel.Customer>
      tableRef={ref}
      properties={tableProperties}
      filter={filterFunc}
      pagination={"advanced"}
      maxRowCount={10}
      rowClickHandler={onRowClick}
      orderByProperty="purchasedAmount"
      orderByDescending
      exportFileName={
        props.exportFileName ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Customer.AllCustomers)
      }
      reloadKey={props.reloadKey}
    />
  );
};
export default React.forwardRef(CustomerList);
